import { Dayjs } from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import {
  cloneData, cloneList, cloneListOfCloneable, DataModel, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { Country } from '../country.js';
import { EventCategory } from '../event-category.js';
import { PhotoReference } from '../file-reference/photo-reference.js';
import { Language } from '../language.js';
import { LocalizedRichDescription } from '../localized-rich-description.js';
import { LocalizedString } from '../localized-string.js';
import { PhotoCollection } from '../photo-collection.js';
import { TicketTemplate } from '../ticket-template/ticket-template.js';
import { TicketTemplateCategory } from '../ticket-template/ticket-template-category.js';
import { TimeZoneRegion } from '../time-zone-region.js';

export enum EventProductionStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export class EventProduction extends DataModel {
  static readonly TYPE_ID: string = '76e9711388ad4704b02e72014ea79d3e';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly status: EventProductionStatus,
    public readonly country: Country,
    public readonly language: Language,
    public readonly timezone: TimeZoneRegion,
    public readonly names: LocalizedString,
    public readonly descriptions: LocalizedRichDescription,
    public readonly ticketInfo: LocalizedRichDescription,
    public readonly tickets: TicketTemplate[],
    public readonly ticketCategories: TicketTemplateCategory[],
    public readonly classification: EventCategory[],
    public readonly artists: string[],
    public readonly brands: string[],
    public readonly organizers: string[],
    public readonly portfolio: PhotoCollection,
    public readonly buildingId: string | null,
    public readonly roomId: string | null,
    public readonly seatingLayoutId: string | null,
  ) {
    super();
  }

  get $typeId(): string {
    return EventProduction.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(EventProduction.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    creatorId: string,
    creationDate: Dayjs,
    businessRef: BusinessModuleRef,
    status: EventProductionStatus,
    country: Country,
    language: Language,
    timezone: TimeZoneRegion,
    names: LocalizedString,
    descriptions: LocalizedRichDescription,
    ticketInfo: LocalizedRichDescription,
    tickets: TicketTemplate[],
    ticketCategories: TicketTemplateCategory[],
    classification: EventCategory[],
    artists: string[],
    brands: string[],
    organizers: string[],
    portfolio: PhotoCollection,
    buildingId: string | null,
    roomId: string | null,
    seatingLayoutId: string | null,
  }): EventProduction {
    return new EventProduction(
        args.uniqueId,
        args.creatorId,
        args.creationDate,
        args.businessRef,
        args.status,
        args.country,
        args.language,
        args.timezone,
        args.names,
        args.descriptions,
        args.ticketInfo,
        args.tickets,
        args.ticketCategories,
        args.classification,
        args.artists,
        args.brands,
        args.organizers,
        args.portfolio,
        args.buildingId,
        args.roomId,
        args.seatingLayoutId,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventProduction {
    return new EventProduction(
        MapUtils.getString(map, 'uniqueId'),
        MapUtils.getString(map, 'creatorId'),
        context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
        BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
        toEnumFromString(MapUtils.getString(map, '$status'), EventProductionStatus),
        toEnumFromString(MapUtils.getString(map, 'country'), Country),
        toEnumFromString(MapUtils.getString(map, 'language'), Language),
        toEnumFromString(MapUtils.getString(map, 'timezone'), TimeZoneRegion),
        context.fromData<LocalizedString>(MapUtils.getRecord(map, 'names')),
        context.fromData<LocalizedRichDescription>(MapUtils.getRecord(map, 'descriptions')),
        context.fromData<LocalizedRichDescription>(MapUtils.getRecord(map, 'ticketInfo')),
        MapUtils.getList<TicketTemplate>(map, 'tickets', (value) => {
          return context.fromData<TicketTemplate>(JSONUtils.decodeRecord(value));
        }),
        MapUtils.getList<TicketTemplateCategory>(map, 'ticketCategories', (value) => {
          return context.fromData<TicketTemplateCategory>(JSONUtils.decodeRecord(value));
        }),
        MapUtils.getList<EventCategory>(map, 'classification', (value) => {
          return toEnumFromString(JSONUtils.decodeString(value), EventCategory);
        }),
        MapUtils.getList<string>(map, 'artists', JSONUtils.decodeString),
        MapUtils.getList<string>(map, 'brands', JSONUtils.decodeString),
        MapUtils.getList<string>(map, 'organizers', JSONUtils.decodeString),
        context.fromData<PhotoCollection>(MapUtils.getRecord(map, 'portfolio')),
        MapUtils.getStringOrNull(map, 'buildingId'),
        MapUtils.getStringOrNull(map, 'roomId'),
        MapUtils.getStringOrNull(map, 'seatingLayoutId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      creationDate: context.encodeDate(this.creationDate),
      businessRef: this.businessRef.encode(),
      '$status': fromEnumAsString(this.status),
      country: fromEnumAsString(this.country),
      language: fromEnumAsString(this.language),
      timezone: fromEnumAsString(this.timezone),
      names: context.toData(this.names),
      descriptions: context.toData(this.descriptions),
      ticketInfo: context.toData(this.ticketInfo),
      tickets: this.tickets.map((ticket) => context.toData(ticket)),
      ticketCategories: this.ticketCategories.map((category) => context.toData(category)),
      classification: this.classification.map(fromEnumAsString),
      artists: this.artists,
      brands: this.brands,
      organizers: this.organizers,
      portfolio: context.toData(this.portfolio),
      buildingId: this.buildingId,
      roomId: this.roomId,
      seatingLayoutId: this.seatingLayoutId,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.creationDate === this.creationDate &&
      isSameData(other.businessRef, this.businessRef) &&
      other.status === this.status &&
      other.country === this.country &&
      other.language === this.language &&
      other.timezone === this.timezone &&
      isSameData(other.names, this.names) &&
      isSameData(other.descriptions, this.descriptions) &&
      isSameData(other.ticketInfo, this.ticketInfo) &&
      isSameList(other.tickets, this.tickets, isSameData) &&
      isSameList(other.ticketCategories, this.ticketCategories, isSameData) &&
      isSameList(other.classification, this.classification) &&
      isSameList(other.artists, this.artists) &&
      isSameList(other.brands, this.brands) &&
      isSameList(other.organizers, this.organizers) &&
      isSameData(other.portfolio, this.portfolio) &&
      other.buildingId === this.buildingId &&
      other.roomId === this.roomId &&
      other.seatingLayoutId === this.seatingLayoutId;
  }

  copyWith({
    status = this.status,
    names = cloneData(this.names),
    descriptions = cloneData(this.descriptions),
    ticketInfo = cloneData(this.ticketInfo),
    tickets = cloneListOfCloneable(this.tickets),
    ticketCategories = cloneListOfCloneable(this.ticketCategories),
    classification = cloneList(this.classification),
    artists = cloneList(this.artists),
    brands = cloneList(this.brands),
    organizers = cloneList(this.organizers),
    photos = cloneListOfCloneable(this.portfolio.photos),
    buildingId = this.buildingId,
    roomId = this.roomId,
    seatingLayoutId = this.seatingLayoutId,
  }: {
    status?: EventProductionStatus,
    names?: LocalizedString,
    descriptions?: LocalizedRichDescription,
    ticketInfo?: LocalizedRichDescription,
    tickets?: TicketTemplate[],
    ticketCategories?: TicketTemplateCategory[],
    classification?: EventCategory[],
    artists?: string[],
    brands?: string[],
    organizers?: string[],
    photos?: PhotoReference[],
    buildingId?: string | null,
    roomId?: string | null,
    seatingLayoutId?: string | null,
  } = {}): this {
    return EventProduction.create({
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      businessRef: cloneData(this.businessRef),
      status,
      country: this.country,
      language: this.language,
      timezone: this.timezone,
      names,
      descriptions,
      ticketInfo,
      tickets,
      ticketCategories,
      classification,
      artists,
      brands,
      organizers,
      portfolio: new PhotoCollection(
          this.portfolio.storagePath,
          this.portfolio.uploadKey,
          photos,
      ),
      buildingId,
      roomId,
      seatingLayoutId,
    }) as this;
  }
}

export class EventProductionBuilder extends DataModelBuilder<EventProduction> {
  build(context: SerializationContext, map: SerializedData): EventProduction {
    return EventProduction.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(_context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(EventProduction.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(EventProduction.SCHEMA_VERSION)) {
      return null;
    }

    let version = versionRange.to;

    // Version 2:
    // - parameter `venueRef` of type `VenueRef | null` was introduced
    if(version < 2) {
      map['venueRef'] ??= null;
      version = 2;
    }

    // Version 3:
    // - parameter `venueRef` was replaced with nullable `buildingId`, `roomId`, and `seatingLayoutId`
    if(version < 3) {
      const venueRef = MapUtils.getRecordOrNull(map, 'venueRef');
      const venueType = venueRef == null ? null : MapUtils.getStringOrNull(venueRef, 'type');

      if(venueRef != null && venueType === 'Seated') {
        map['buildingId'] = MapUtils.getStringOrNull(venueRef, 'buildingId');
        map['roomId'] = MapUtils.getStringOrNull(venueRef, 'roomId');
        map['seatingLayoutId'] = MapUtils.getStringOrNull(venueRef, 'seatingLayoutId');
      }
      else if(venueRef != null && venueType === 'NonSeated') {
        map['buildingId'] = MapUtils.getStringOrNull(venueRef, 'placeId');
        map['roomId'] = null;
        map['seatingLayoutId'] = null;
      }
      else {
        map['buildingId'] = null;
        map['roomId'] = null;
        map['seatingLayoutId'] = null;
      }

      delete map['venueRef'];
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
